/**
 * ImageViewerPanel Component
 *
 * Right panel of session details page displaying:
 * - Selected image with detections
 * - Processing overlay for analyzing images
 * - Error display
 * - Fallback for no image selected
 */

import React from 'react';
import { FileImage, Loader, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Detection, HistoryItem } from '@/types';
import { AnalysisImageViewer } from '@/components/session/AnalysisImageViewer';
import { UploadedImage } from '@/hooks/useImageUpload';

interface ImageViewerPanelProps {
  selectedImageId: string | null;
  uploadedImages: UploadedImage[];
  analyses: HistoryItem[];
  topK: number;
  isSessionCompleted: boolean;
  isDrawingMode: boolean;
  isAdmin: boolean;
  classValidationStatus?: Map<string, 'match' | 'mismatch' | 'unexpected'>;
  newlyCreatedDetectionId: number | null;

  // Handlers
  onClassOverride?: (detectionId: number, newTopClass: string, newScore: number) => void;
  onUserDrawnDetection?: (bbox: [number, number, number, number], imageElement: HTMLImageElement) => void;
  onDeleteDetection?: (detectionId: number) => void;
  onAutoOpenDone: () => void;
  onPrevImage: () => void;
  onNextImage: () => void;
  hasPrevImage: boolean;
  hasNextImage: boolean;

  // Image loading
  loadAnalysisImage: (id: number) => Promise<string | null>;
}

export const ImageViewerPanel: React.FC<ImageViewerPanelProps> = ({
  selectedImageId,
  uploadedImages,
  analyses,
  topK,
  isSessionCompleted,
  isDrawingMode,
  isAdmin,
  classValidationStatus,
  newlyCreatedDetectionId,
  onClassOverride,
  onUserDrawnDetection,
  onDeleteDetection,
  onAutoOpenDone,
  onPrevImage,
  onNextImage,
  hasPrevImage,
  hasNextImage,
  loadAnalysisImage,
}) => {
  const { t } = useTranslation();

  if (!selectedImageId) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 flex-1 min-h-0 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <FileImage className="h-12 w-12 mx-auto mb-2" />
          <p>{t("session_details.select_image_instruction")}</p>
        </div>
      </div>
    );
  }

  const uploadedImage = uploadedImages.find((img) => img.id === selectedImageId);
  const analysisImage = selectedImageId.startsWith("analysis-")
    ? analyses.find((a) => `analysis-${a.id}` === selectedImageId)
    : null;

  // Uploaded image (in-progress upload)
  if (uploadedImage) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 flex-1 min-h-0">
        <div className="h-full flex flex-col relative">
          <div className="flex-1 min-h-0">
            <AnalysisImageViewer
              imageFile={uploadedImage.file}
              detections={uploadedImage.result?.detections || []}
              className="h-full w-full"
              topK={topK}
              onClassOverride={isSessionCompleted ? undefined : onClassOverride}
              isDrawingMode={isSessionCompleted ? false : isDrawingMode}
              onUserDrawnDetection={isSessionCompleted ? undefined : onUserDrawnDetection}
              onDeleteDetection={isSessionCompleted ? undefined : onDeleteDetection}
              autoOpenDetectionId={newlyCreatedDetectionId}
              onAutoOpenDone={onAutoOpenDone}
              onPrevImage={onPrevImage}
              onNextImage={onNextImage}
              hasPrevImage={hasPrevImage}
              hasNextImage={hasNextImage}
              isAdmin={isAdmin}
              imageFileName={uploadedImage.file.name}
              classValidationStatus={classValidationStatus}
            />
          </div>

          {uploadedImage.isAnalyzing && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 rounded-lg">
              <div className="bg-white rounded-lg p-6 text-center shadow-xl">
                <Loader className="h-8 w-8 mx-auto mb-4 animate-spin text-primary-600" />
                <p className="text-gray-700 font-medium">
                  {t("session_details.analyzing_image")}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t("session_details.please_wait")}
                </p>
              </div>
            </div>
          )}

          {uploadedImage.error && (
            <div className="absolute top-4 left-4 right-4 bg-red-50 border border-red-200 rounded-md p-4 z-40">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <span className="text-red-800">{uploadedImage.error}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Analysis image (saved analysis)
  if (analysisImage) {
    // Handle processing images specially
    if (analysisImage.isProcessing && analysisImage.uploadData) {
      return (
        <div className="bg-white rounded-lg shadow-sm border p-6 flex-1 min-h-0">
          <div className="h-full flex flex-col relative">
            <div className="flex-1 min-h-0">
              <AnalysisImageViewer
                imageFile={analysisImage.uploadData.file}
                detections={[]}
                className="h-full w-full"
                topK={topK}
                onClassOverride={undefined}
                isDrawingMode={false}
                onUserDrawnDetection={undefined}
                onDeleteDetection={undefined}
                isAdmin={isAdmin}
                imageFileName={analysisImage.image_name}
                classValidationStatus={classValidationStatus}
              />
            </div>
            {/* Processing overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
              <div className="bg-white rounded-lg p-6 text-center shadow-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-lg font-medium text-gray-900">
                  {t("session_details.analyzing")}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t("session_details.please_wait")}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Completed analysis with detections
    if (analysisImage.detections) {
      return (
        <div className="bg-white rounded-lg shadow-sm border p-6 flex-1 min-h-0">
          <div className="h-full">
            <AnalysisImageViewer
              analysisId={analysisImage.id}
              detections={analysisImage.detections}
              loadImage={loadAnalysisImage}
              className="h-full w-full"
              topK={topK}
              onClassOverride={isSessionCompleted ? undefined : onClassOverride}
              isDrawingMode={isSessionCompleted ? false : isDrawingMode}
              onUserDrawnDetection={isSessionCompleted ? undefined : onUserDrawnDetection}
              onDeleteDetection={isSessionCompleted ? undefined : onDeleteDetection}
              autoOpenDetectionId={newlyCreatedDetectionId}
              onAutoOpenDone={onAutoOpenDone}
              onPrevImage={onPrevImage}
              onNextImage={onNextImage}
              hasPrevImage={hasPrevImage}
              hasNextImage={hasNextImage}
              isAdmin={isAdmin}
              imageFileName={analysisImage.image_name}
              classValidationStatus={classValidationStatus}
            />
          </div>
        </div>
      );
    }

    // Fallback for analysis without detections
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 flex-1 min-h-0 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <FileImage className="h-12 w-12 mx-auto mb-2" />
          <p>{t("session_details.no_image_available")}</p>
        </div>
      </div>
    );
  }

  // No matching image found
  return (
    <div className="bg-white rounded-lg shadow-sm border p-6 flex-1 min-h-0 flex items-center justify-center text-gray-500">
      <div className="text-center">
        <FileImage className="h-12 w-12 mx-auto mb-2" />
        <p>{t("session_details.select_image_instruction")}</p>
      </div>
    </div>
  );
};

export default ImageViewerPanel;
